import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import Button from "../../Components/Button";
import RequiredCard from "../../Components/RequiredCard";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { PatternFormat } from "react-number-format";
import { ControlledInput } from "../../Components/ControlledInput";
import { ControlledAutoComplete } from "../../Components/ControlledAutocomplete";
import EnquiryInput from "../../Components/EnquiryInput";
import { FORM_STEP } from "../EnquiryV3";

interface IParentEnquiryProps {
    [key: string]: any;
}
interface FormType {
    parentFirstName: string;
    parentLastName: string;
    email: string;
    contactNumber: string;
    suburb: object;
}
const formatLandline = "(##) #### ####";
const formatPhoneNumber = "#### ### ###";
const validAustralianAreaCodes = ["02", "03", "07", "08"];
const handleCheckValidAustralianAreaCode = (code: string) => {
    return validAustralianAreaCodes.includes(code);
};
function containsSpecialCharacters(input: string) {
    const specialCharRegex = /[^a-zA-Z0-9 ]/;
    return specialCharRegex.test(input);
}

const validationSchema = Yup.object().shape({
    parentFirstName: Yup.string().required("First name is required"),
    parentLastName: Yup.string().required("Last name is required"),
    email: Yup.string().required("Email is required").email("Email is invalid"),
    contactNumber: Yup.string()
        .required("Phone number is required")
        .transform((value) => value.replace(/[^0-9]/g, ""))
        .min(10, "Phone number must be 10 digit")
        .test(
            "valid-phone",
            "Invalid phone number. Must be either a normal phone number (starting with 02, 03, or 04) or an international phone number (starting with +61).",
            (value) => {
                if (!value) return false; // Ensure value is not empty
                const normalPhoneRegex = /^0[2378]\d{8}$/; // Normal phone number
                const internationalPhoneRegex = /^0[2478]\d{8}$/; // International phone number
                return (
                    normalPhoneRegex.test(value) || internationalPhoneRegex.test(value)
                );
            }
        ),
    suburb: Yup.mixed()
        .required("Suburb is required")
        .test("is-object", "Please select a valid suburb", (value: any) => {
            if (!value || typeof value !== "object") return false;
            return Object.keys(value).length > 0 && value.postcode;
        })
        .transform((value) => {
            // If value is empty string or not an object, return null
            if (!value || typeof value !== "object") return null;
            return value;
        }),
});
function ParentDetail(props: IParentEnquiryProps) {
    const [isInvalidForm, setIsInvalidForm] = useState(false);

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors, isSubmitted },
        trigger,
    } = useForm<FormType>({
        mode: "onBlur",
        reValidateMode: "onBlur",
        resolver: yupResolver(validationSchema),
        defaultValues: {
            parentFirstName:
                props?.enquiryDetail?.parentDetail?.parentFirstName || "",
            parentLastName: props?.enquiryDetail?.parentDetail?.parentLastName || "",
            email: props?.enquiryDetail?.parentDetail?.email || "",
            contactNumber: props?.enquiryDetail?.parentDetail?.contactNumber || "",
            suburb: props?.enquiryDetail?.parentDetail?.suburb || {},
        },
    });
    useEffect(() => {
        props.sendEventGa4({
            eventName: "enquiry_workflow",
            FormStep: FORM_STEP[1],
            Action: "start",
            ListingType: props.listingType,
        });
    }, []);
    const contactNumber = watch("contactNumber").replace(/[^0-9]/g, "");
    const isContactNumberNotContainSepecialCharacters =
        !containsSpecialCharacters(contactNumber);
    const isValidAustralianAreaCode = handleCheckValidAustralianAreaCode(
        contactNumber.substring(0, 2)
    );
    const isAustralianMobilePhoneNumber = contactNumber.startsWith("04");

    const currentFormat =
        contactNumber && isValidAustralianAreaCode
            ? formatLandline
            : formatPhoneNumber;

    const onSubmit = (data) => {
        props.sendEventGa4({
            eventName: "enquiry_workflow",
            FormStep: FORM_STEP[1],
            Action: "finish",
            ListingType: props.listingType,
        });
        //props.submitGa4()
        props.onNext({
            data: {
                ...data,
                parentFullName: data.parentFirstName + " " + data.parentLastName,
                isValidAustralianAreaCode,
            },
        });
    };
    const onError = (e) => {
        const invalidArray = [];

        if (e.parentFirstName?.type === "required") {
            invalidArray.push("missing - parent first name");
        }

        if (e.parentLastName?.type === "required") {
            invalidArray.push("missing - parent last name");
        }

        if (e.email?.type === "required") {
            invalidArray.push("missing - parent email");
        } else if (e.email?.type === "email") {
            invalidArray.push("invalid - parent email");
        }

        if (e.contactNumber?.type === "required") {
            invalidArray.push("missing - parent phone");
        } else if (
            e.contactNumber?.type === "valid-phone" ||
            e.contactNumber?.type === "min"
        ) {
            invalidArray.push("invalid - parent phone");
        }

        if (e.suburb?.type === "required") {
            invalidArray.push("missing - parent suburb");
        } else if (e.suburb?.type === "is-object") {
            invalidArray.push("invalid - parent suburb");
        }
        invalidArray.forEach((item) => {
            props.sendEventGa4({
                eventName: "enquiry_workflow_feature",
                FeatureName: "validation error - parent details",
                FeatureData: item,
            });
        });

        setIsInvalidForm(true);
    };

    const handleBlur = (fieldName) => {
        trigger(fieldName);
        if (!isSubmitted) return;
        setIsInvalidForm(Object.keys(errors).length > 0);
        // if (Object.keys(errors).length > 0) {
        //     setIsInvalidForm(true)
        // }
        // else {
        //     setIsInvalidForm(false)
        // }
    };

    return (
        <div className="font-poppins pb-24 lg:pb-0">
            <h2 className="font-semibold text-[22px] text-[#3A3A3A] mb-8 block">
                Share your details with the centre
            </h2>

            {isInvalidForm && (
                <RequiredCard customClass={"mb-8 md:max-w-[400px]"}>
                    <Icon
                        icon="fa6-solid:circle-exclamation"
                        width="16"
                        height="16"
                        className="flex-shrink-0 text-[#EA5743]"
                    />
                    <p className="text-[#EA5743] text-base">
                        <span className="font-semibold">
                            Some fields require your input.
                        </span>
                        Please complete all mandatory fields to proceed.
                    </p>
                </RequiredCard>
            )}
            <form onSubmit={handleSubmit(onSubmit, onError)}>
                <div className="md:max-w-[400px] space-y-4">
                    <ControlledInput
                        control={control}
                        label="Parent's First Name"
                        name="parentFirstName"
                        inputProps={{
                            id: "fName",
                            placeholder: "Enter First Name",
                            onBlur: () => {
                                handleBlur("parentFirstName");
                            },
                        }}
                    />

                    <ControlledInput
                        control={control}
                        label="Parent's Last Name"
                        name="parentLastName"
                        inputProps={{
                            id: "lName",
                            placeholder: "Enter Last Name",
                            onBlur: () => {
                                handleBlur("parentLastName");
                            },
                        }}
                    />

                    <ControlledInput
                        control={control}
                        label="Email"
                        name="email"
                        inputProps={{
                            id: "email",
                            placeholder: "Enter Email Address",
                            onBlur: () => {
                                handleBlur("email");
                            },
                        }}
                    />
                    <div>
                        <ControlledInput
                            control={control}
                            label="Contact Number"
                            name="contactNumber"
                            inputProps={{
                                id: "phoneNumber",
                                onBlur: () => {
                                    handleBlur("contactNumber");
                                },
                            }}
                            renderInput={({ ref, ...props }) => (
                                <PatternFormat
                                    {...props}
                                    type="tel"
                                    format={currentFormat}
                                    placeholder="xxxx xxx xxx"
                                    customInput={EnquiryInput}
                                    getInputRef={ref}
                                />
                            )}
                        />

                        <div className="pl-6 mt-1 space-y-1">
                            {!isValidAustralianAreaCode && (
                                <ErrorContactNumber isError={isAustralianMobilePhoneNumber}>
                                    Mobile: start with '04'.
                                </ErrorContactNumber>
                            )}

                            {!isAustralianMobilePhoneNumber && (
                                <ErrorContactNumber isError={isValidAustralianAreaCode}>
                                    Landline: start with area code in your location.
                                </ErrorContactNumber>
                            )}

                            <ErrorContactNumber
                                isError={isContactNumberNotContainSepecialCharacters}
                            >
                                No special characters allowed
                            </ErrorContactNumber>
                        </div>
                    </div>

                    <ControlledAutoComplete
                        control={control}
                        label="Suburb"
                        name="suburb"
                        inputProps={{
                            id: "suburb",
                            placeholder: "Enter Suburb Or Postcode",
                        }}
                        handleBlur={handleBlur}
                    />
                </div>
                <div className="fixed bottom-0 left-0 right-0 bg-white p-4 mt-0 md:pt-10 border-t border-gray-200 flex justify-center z-10 lg:static lg:bg-transparent lg:border-0 lg:justify-center lg:ml-[-340px]">
                    <Button
                        type="submit"
                        variant="primary"
                        customClass="w-full lg:w-fit my-0 lg:md:mb-0 parent-detail next-button-step-2"
                    >
                        Next
                    </Button>
                </div>
            </form>
        </div>
    );
}

const ErrorContactNumber = ({
    isError,
    children,
}: {
    isError: boolean;
    children: React.ReactNode;
}) => (
    <div className="flex items-center gap-2">
        {isError ? (
            <Icon
                icon="teenyicons:tick-circle-outline"
                width="15"
                height="15"
                style={{ color: "#3a7936" }}
                className="flex-shrink-0"
            />
        ) : (
            <Icon
                icon="teenyicons:x-circle-outline"
                width="15"
                height="15"
                style={{ color: "#EA5743" }}
                className="flex-shrink-0"
            />
        )}
        <p
            className={`text-sm font-normal ${isError ? "text-[#3a7936]" : "text-[#EA5743]"
                }`}
        >
            {children}
        </p>
    </div>
);

export default ParentDetail;
